import { categoryRunRates, categorySpend, type BudgetLine, type ExpenseCategory } from "@torre/expenses";
import { budgetLinesFor } from "./budget";

// ---------------------------------------------------------------------------
// Presupuesto en el tiempo (#1066): el plan de cada mes del año contra lo que
// se gastó, y una proyección del resto del año con el ritmo de gasto reciente.
//
// Ritmo, por categoría (#1178, regla compartida en @torre/expenses): promedio
// de los últimos 3 meses CERRADOS con gasto (hasta 6 meses atrás); nómina, el
// último mes cerrado. Un mes sin estado de cuenta cargado no lo jala a cero.
// Los meses que faltan se proyectan con ese ritmo; el mes en curso cuenta lo
// que ya se gastó o el ritmo, lo que sea mayor.
// ---------------------------------------------------------------------------

type SpentLike = { period: string; category: string; amountBase: number | null | undefined };

export type MonthPhase = "past" | "current" | "future";

export interface TimelineMonth {
  period: string;
  phase: MonthPhase;
  /** Presupuesto del mes (línea mensual o anual prorrateada a 1/12). */
  budget: number;
  budgetByCategory: Partial<Record<ExpenseCategory, number>>;
  /** Gastado real (null en meses por venir). */
  spent: number | null;
  /** Gasto esperado: real en meses cerrados; ritmo en el resto. */
  expected: number;
  /** Solo lo proyectado (meses en curso y por venir); null en meses cerrados. */
  projected: number | null;
  /** Acumulados desde el primer mes con plan (null antes). */
  cumBudget: number | null;
  cumSpent: number | null;
  cumExpected: number | null;
}

/** «risk»: va en rango hoy, pero al ritmo actual cerraría arriba de su plan. */
export type CategoryStatus = "ok" | "warn" | "risk" | "over" | "unbudgeted" | "idle";

export interface CategoryOutlook {
  category: ExpenseCategory;
  budget: number;
  spent: number;
  /** Cierre de año esperado con el ritmo actual. */
  projected: number;
  runRate: number;
  pct: number | null;
  /** % ya transcurrido de los meses en que la categoría se mide (los suyos con presupuesto). */
  elapsedPct: number;
  status: CategoryStatus;
}

export interface BudgetTimeline {
  /** El año si el periodo es un año calendario; si no, el mes o «desde – hasta». */
  year: string;
  /** Primer y último mes del periodo mostrado. */
  from: string;
  to: string;
  /** Posición (desde 1) del mes en curso en `months`; 0 si el periodo ya terminó; `months.length + 1` si no empieza. */
  currentIndex: number;
  months: TimelineMonth[];
  categories: CategoryOutlook[];
  totalBudget: number;
  /** Gastado que se compara contra el plan (hasta hoy): la suma de `categories[].spent`. */
  spentToDate: number;
  /** Cierre esperado de lo que se compara: real + proyección; la suma de `categories[].projected`. */
  projectedYearEnd: number;
  /** Gastado en todo el periodo, con o sin plan. */
  spentYear: number;
  /** Primer y último mes con presupuesto (null sin plan). */
  planFrom: string | null;
  planTo: string | null;
  planMonths: number;
  /** % del periodo del plan que ya transcurrió. */
  elapsedPct: number;
  runRate: number;
  /** Mes en que el gasto acumulado esperado rebasa el presupuesto acumulado del año. */
  overrunMonth: string | null;
}

const round = (n: number) => Math.round(n * 100) / 100;

/** Un año ("2026") o un rango de meses completos (#1332). */
export type TimelineScope = string | { from: string; to: string };

const MAX_SCOPE_MONTHS = 60;

/** Los meses del alcance, en orden. */
export function scopePeriods(scope: TimelineScope): string[] {
  const { from, to } = typeof scope === "string" ? { from: `${scope}-01`, to: `${scope}-12` } : scope;
  const out: string[] = [];
  let y = Number(from.slice(0, 4));
  let m = Number(from.slice(5, 7));
  for (let i = 0; i < MAX_SCOPE_MONTHS; i++) {
    const period = `${y}-${String(m).padStart(2, "0")}`;
    if (period > to) break;
    out.push(period);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out.length ? out : [from];
}

/** El rango es exactamente un año calendario (enero a diciembre). */
export const isCalendarYear = (from: string, to: string) => from.slice(0, 4) === to.slice(0, 4) && from.endsWith("-01") && to.endsWith("-12");

/**
 * Gastos que cuentan contra el plan del periodo: de cada categoría con plan,
 * solo los de sus meses con plan; las categorías sin plan pasan completas (se
 * ven como «Sin presupuesto»). Evita comparar todo el año contra un plan de
 * oct–dic.
 */
export function expensesInPlanWindow<T extends SpentLike>(lines: readonly BudgetLine[], expenses: readonly T[], scope: TimelineScope): T[] {
  const planned = new Map<string, Set<string>>();
  for (const period of scopePeriods(scope)) {
    for (const l of budgetLinesFor(lines, period)) {
      if (l.amount <= 0) continue;
      const set = planned.get(l.category) ?? new Set<string>();
      set.add(period);
      planned.set(l.category, set);
    }
  }
  return expenses.filter((e) => {
    const months = planned.get(e.category);
    return !months || months.has(e.period);
  });
}

/** Umbral a partir del cual una categoría está «cerca del límite». */
export const WARN_AT = 85;

export function categoryStatus(budget: number, amount: number): CategoryStatus {
  if (budget <= 0) return amount > 0 ? "unbudgeted" : "idle";
  const pct = (amount / budget) * 100;
  if (pct > 100) return "over";
  if (pct >= WARN_AT) return "warn";
  return "ok";
}

export function budgetTimeline(lines: readonly BudgetLine[], expenses: readonly SpentLike[], scope: TimelineScope, today: string): BudgetTimeline {
  const periods = scopePeriods(scope);
  const first = periods[0];
  const last = periods[periods.length - 1];
  const year = isCalendarYear(first, last) ? first.slice(0, 4) : first === last ? first : `${first} – ${last}`;
  const nowPeriod = today.slice(0, 7);
  // Posición (desde 1) del mes en curso; 0 si el periodo ya terminó; uno más que el último si no empieza.
  const currentIndex = nowPeriod < first ? periods.length + 1 : nowPeriod > last ? 0 : periods.indexOf(nowPeriod) + 1;
  // Un periodo que ya terminó es todo pasado; uno que no empieza, todo por venir.
  const phaseOf = (i: number): MonthPhase => (currentIndex === 0 ? "past" : currentIndex > periods.length ? "future" : i + 1 < currentIndex ? "past" : i + 1 === currentIndex ? "current" : "future");

  // Gastado por mes y categoría (solo gastos con monto en pesos). El resto de
  // `expenses` (meses fuera del periodo) solo alimenta el ritmo de gasto.
  const spent = new Map<string, Map<string, number>>();
  for (const e of expenses) {
    if (e.amountBase == null || e.period < first || e.period > last) continue;
    const m = spent.get(e.period) ?? new Map<string, number>();
    m.set(e.category, (m.get(e.category) || 0) + e.amountBase);
    spent.set(e.period, m);
  }

  // Presupuesto por mes y categoría, con la misma regla que la tabla.
  const budgetBy = periods.map((p) => {
    const out: Partial<Record<ExpenseCategory, number>> = {};
    for (const l of budgetLinesFor(lines, p)) out[l.category] = (out[l.category] || 0) + l.amount;
    return out;
  });

  const categories = new Set<ExpenseCategory>();
  budgetBy.forEach((b) => Object.keys(b).forEach((c) => categories.add(c as ExpenseCategory)));
  spent.forEach((m) => m.forEach((_, c) => categories.add(c as ExpenseCategory)));

  // Ritmo por categoría: la regla compartida (#1178, @torre/expenses categoryRunRates),
  // la misma que usan las gráficas de Gastos y el ajuste semanal de presupuestos.
  const rateRows = expenses
    .filter((e) => e.amountBase != null)
    .map((e) => ({ date: `${e.period}-01`, category: e.category as ExpenseCategory, amountBase: e.amountBase as number }));
  const shared = categoryRunRates(categorySpend(rateRows).months, today);
  const runRates = new Map<ExpenseCategory, number>();
  for (const c of categories) runRates.set(c, shared[c]?.rate || 0);
  const runRate = round([...runRates.values()].reduce((s, v) => s + v, 0));

  // Ventana del plan: los meses que tienen presupuesto. Si el plan solo cubre
  // oct–dic, se compara contra lo gastado y lo proyectado en oct–dic, no contra
  // todo el año. Sin ningún presupuesto, la ventana es el año completo.
  const monthBudget = budgetBy.map((b) => round(Object.values(b).reduce((s, v) => s + (v || 0), 0)));
  const planned = monthBudget.map((b) => b > 0);
  const hasPlan = planned.some(Boolean);
  const inWindow = (i: number) => !hasPlan || planned[i];
  const windowStart = hasPlan ? planned.indexOf(true) : 0;

  const expectedOf = (i: number, c: ExpenseCategory) => {
    const real = spent.get(periods[i])?.get(c) || 0;
    const phase = phaseOf(i);
    return phase === "past" ? real : phase === "current" ? Math.max(real, runRates.get(c) || 0) : runRates.get(c) || 0;
  };

  // En qué meses se mide cada categoría: en los suyos con presupuesto; una sin
  // presupuesto, en los meses en que el plan existe. Así el gasto de meses sin
  // ningún plan no se compara contra un presupuesto que no era suyo, y los
  // totales, el acumulado y la tabla por categoría usan la misma regla.
  const countsOf = new Map<ExpenseCategory, (i: number) => boolean>();
  for (const c of categories) {
    const own = periods.map((_, i) => (budgetBy[i][c] || 0) > 0);
    const hasOwn = own.some(Boolean);
    countsOf.set(c, (i) => (hasOwn ? own[i] : inWindow(i)));
  }
  const counts = (c: ExpenseCategory, i: number) => (countsOf.get(c) as (i: number) => boolean)(i);

  let cumBudget = 0;
  let cumSpent = 0;
  let cumExpected = 0;
  const months: TimelineMonth[] = periods.map((period, i) => {
    const phase = phaseOf(i);
    const budget = monthBudget[i];
    const real = [...(spent.get(period)?.values() ?? [])].reduce((s, v) => s + v, 0);
    const expected = [...categories].reduce((s, c) => s + expectedOf(i, c), 0);
    const counted = i >= windowStart;
    if (counted) {
      cumBudget += budget;
      // El acumulado solo suma lo que se compara contra el plan.
      for (const c of categories) {
        if (!counts(c, i)) continue;
        cumExpected += expectedOf(i, c);
        if (phase !== "future") cumSpent += spent.get(period)?.get(c) || 0;
      }
    }
    return {
      period,
      phase,
      budget,
      budgetByCategory: budgetBy[i],
      spent: phase === "future" ? null : round(real),
      expected: round(expected),
      projected: phase === "past" ? null : round(expected),
      cumBudget: counted ? round(cumBudget) : null,
      cumSpent: counted && phase !== "future" ? round(cumSpent) : null,
      cumExpected: counted ? round(cumExpected) : null,
    };
  });

  const outlook: CategoryOutlook[] = [...categories]
    .map((c) => {
      const budget = round(periods.reduce((sum, _, i) => sum + (budgetBy[i][c] || 0), 0));
      const s = round(periods.reduce((sum, p, i) => sum + (counts(c, i) && phaseOf(i) !== "future" ? spent.get(p)?.get(c) || 0 : 0), 0));
      const projected = round(periods.reduce((sum, _, i) => sum + (counts(c, i) ? expectedOf(i, c) : 0), 0));
      const own = periods.map((_, i) => i).filter((i) => counts(c, i));
      const gone = own.reduce((sum, i) => sum + (phaseOf(i) === "past" ? 1 : phaseOf(i) === "current" ? 0.5 : 0), 0);
      const today = categoryStatus(budget, s);
      const status: CategoryStatus = (today === "ok" || today === "warn") && projected > budget ? "risk" : today;
      return {
        category: c,
        budget,
        spent: s,
        projected,
        runRate: round(runRates.get(c) || 0),
        pct: budget > 0 ? (s / budget) * 100 : null,
        elapsedPct: own.length ? (gone / own.length) * 100 : 0,
        status,
      };
    })
    .filter((r) => r.budget > 0 || r.spent > 0 || r.projected > 0)
    .sort((a, b) => b.budget + b.spent - (a.budget + a.spent));
  // Los totales son la suma de los renglones: lo que dice el resumen cuadra con la tabla.
  const spentRows = round(outlook.reduce((s, r) => s + r.spent, 0));
  const projectedRows = round(outlook.reduce((s, r) => s + r.projected, 0));

  const overrun = months.find((m) => m.cumBudget != null && m.cumExpected != null && m.cumBudget > 0 && m.cumExpected > m.cumBudget && m.phase !== "past");
  const windowIdx = periods.map((_, i) => i).filter(inWindow);
  // Cuánto del periodo del plan ya pasó: meses cerrados completos + medio mes en curso.
  const elapsed = windowIdx.reduce((s, i) => s + (phaseOf(i) === "past" ? 1 : phaseOf(i) === "current" ? 0.5 : 0), 0);

  return {
    year,
    from: first,
    to: last,
    currentIndex,
    months,
    categories: outlook,
    // Suma sin redondear por mes: una línea anual de 100,000 da 100,000, no 99,999.96.
    totalBudget: round(budgetBy.reduce((s, b) => s + Object.values(b).reduce((x, v) => x + (v || 0), 0), 0)),
    spentToDate: spentRows,
    projectedYearEnd: projectedRows,
    spentYear: round(months.reduce((s, m) => s + (m.spent ?? 0), 0)),
    planFrom: hasPlan ? periods[windowIdx[0]] : null,
    planTo: hasPlan ? periods[windowIdx[windowIdx.length - 1]] : null,
    planMonths: hasPlan ? windowIdx.length : 0,
    elapsedPct: windowIdx.length ? (elapsed / windowIdx.length) * 100 : 0,
    runRate,
    overrunMonth: overrun?.period ?? null,
  };
}
